import React, { useMemo, useCallback } from 'react';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import {
	COMPANY_MANAGED_PROJECT,
	TEAM_MANAGED_PROJECT,
	convertToJiraProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import {
	type LinkResolverOptions,
	SummaryFeaturesProvider,
} from '@atlassian/jira-contextual-summary/src/controllers/features-context/index.tsx';
import { SummaryPage } from '@atlassian/jira-contextual-summary/src/ui/index.tsx';
import { generateListViewParams } from '@atlassian/jira-contextual-summary/src/ui/utils.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { RedirectToLoginPage } from '@atlassian/jira-spa-apps-common/src/redirect/login/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';

const JiraSoftwareSummaryApp = () => {
	const accountId = useAccountId();
	const licensedProducts = useLicensedProducts();
	const { data: projectContextData } = useProjectContext();

	const projectData = useMemo(() => {
		if (!projectContextData) {
			return null;
		}

		return {
			key: projectContextData.projectKey,
			projectId: String(projectContextData.projectId),
			name: projectContextData.projectName,
			projectStyle: projectContextData.simplified ? TEAM_MANAGED_PROJECT : COMPANY_MANAGED_PROJECT,
			projectType: convertToJiraProjectType(projectContextData.projectType),
		};
	}, [projectContextData]);

	const linksResolver = useCallback(
		(options: LinkResolverOptions) => `./list?${generateListViewParams(options)}`,
		[],
	);

	if (!projectData) {
		return <UFOLoadHold name="jira-summary-use-features" />;
	}

	if (!accountId) {
		return <RedirectToLoginPage />;
	}

	const hasSoftwareLicense = licensedProducts?.[LICENSED_PRODUCTS.JIRA_SOFTWARE];
	if (!hasSoftwareLicense) {
		return <Redirect to="/jira/projects" />;
	}

	return (
		<AuthenticationCheck>
			<AppBase id="jira-summary">
				<SummaryFeaturesProvider linksResolver={linksResolver}>
					<SummaryPage projectData={projectData} />
				</SummaryFeaturesProvider>
			</AppBase>
		</AuthenticationCheck>
	);
};

export default JiraSoftwareSummaryApp;
